/*
  Market gate — client-side half.

  Removes [data-market-only="US"] elements from visitors who are not in the US.
  The server cannot do this: no market on this store owns a domain or subfolder,
  so everyone resolves to the primary market (North America) regardless of where
  they are browsing from. See snippets/market-gate.liquid for the measurement.

  The real country comes from the IP lookup theme.js already performs
  (api.country.is -> sessionStorage.detectedCountry, plus a country:detected
  event). This file only reads those values — it never writes them and does not
  import from the existing consumers, so nothing else changes behaviour.

  Contracts below are copied from the established consumers rather than shared,
  so a change here cannot break PLP media or the size guide.
*/
(function () {
  if (window.__verishMarketGate) return; /* drawer + cart page can both load this */
  window.__verishMarketGate = true;

  var ATTR = 'data-market-only';

  /* The mirror image of ATTR: show everywhere EXCEPT the listed countries.
     An allow-list cannot express "everyone but the US" — with 198 markets the
     list would have to be enumerated and would rot the moment one is added.

     Elements carrying this attribute are rendered `hidden` by the server, because
     the server believes everyone is in the US (see snippets/market-gate.liquid).
     Unhiding is this file's job.

     쓰는 곳 둘: 미국 제외 프로모션 배지, 그리고 MCF 표기가 대신 들어간 자리의
     테마 기본 배송 문구 — 후자는 복원하지 않으면 화면에 아무것도 안 남는다. */
  var EXCEPT_ATTR = 'data-market-except';

  /* The shopper's own country pick. Shopify records it in the localization
     cookie, but that cookie reads US for someone who never touched the selector
     too (measured 2026-08-10: a first request with no cookies comes back
     `set-cookie: localization=US`), so it cannot tell a choice from a default.
     Hence our own record.

     🔴 sessionStorage, not localStorage. The pick is deliberately volatile — it
     dies with the tab, the same lifetime the IP answer already has under
     `detectedCountry`, so the two signals expire together. localStorage kept a
     US pick alive forever: the cookie it is checked against is US by default,
     so the contradiction that prunes it never came, and shoppers stayed gated
     as US in every later session.

     🔴 An in-memory variable cannot do this job. Picking submits the form and
     navigates (theme.js LocalizationForm), so a plain variable dies on the very
     navigation the pick causes and never reaches the page that needs it.

     Measurements and case table: local/mcf/2026-08-05-market-gate-two-layer.md */
  var PICK_KEY = 'verish-country-pick';

  function readPick() {
    try {
      return (sessionStorage.getItem(PICK_KEY) || '').trim().toUpperCase();
    } catch (e) {
      return ''; /* private mode throws on access */
    }
  }

  /* The country Shopify has in force. Never opens the gate — it reads US for
     everyone — it can only contradict the pick. */
  function cookieCountry() {
    var match = document.cookie.match(/(?:^|;\s*)localization=([^;]+)/);
    return match ? decodeURIComponent(match[1]).trim().toUpperCase() : '';
  }

  /* 🔴 Not a plain getter: drops the stored pick when the cookie contradicts it,
     which is what keeps our record from outliving the choice Shopify honours.
     An unreadable cookie is silence, not contradiction, so the pick stands. */
  function honouredPick() {
    var stored = readPick();
    if (!stored) return '';

    var cookie = cookieCountry();
    if (cookie && cookie !== stored) {
      try {
        sessionStorage.removeItem(PICK_KEY);
      } catch (e) {}
      return '';
    }
    return stored;
  }

  /* 🔴 On the click, not on submit: the selector calls form.submit() directly
     (theme.js LocalizationForm.onItemClick) and that fires no submit event.
     Delegated because each selector holds ~196 options; capture phase so an
     upstream stopPropagation cannot cost us the record.

     🔴 data-disclosure-country is the theme's own marker and appears on the
     country selector only — the language selector shares everything else. If a
     theme update drops it, picks stop being recorded silently and the gate
     quietly reverts to judging by IP. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var option = target.closest('[data-disclosure-country] [data-disclosure-option]');
      if (!option) return;

      var code = (option.getAttribute('data-value') || '').trim().toUpperCase();
      if (!code) return;
      try {
        sessionStorage.setItem(PICK_KEY, code);
      } catch (e) {
        /* private mode — the gate falls back to path and IP, as before */
      }
    },
    true
  );

  function cachedCountry() {
    var fromMemory = window.FoxTheme && window.FoxTheme.country;
    if (fromMemory) return fromMemory;
    try {
      return sessionStorage.getItem('detectedCountry') || '';
    } catch (e) {
      return ''; /* private mode throws on access */
    }
  }

  function isTwPath(pathname) {
    return pathname === '/tw' || pathname.indexOf('/tw/') === 0;
  }

  /* Precedence: pick, then path, then IP.

     The pick wins because choosing United States really does switch the market
     — currency, cart currency and Global-e all follow, so judging that shopper
     by their IP would leave them a USD checkout with nothing explaining it. It
     beats the path too: /tw/ is a Weglot language path, and the language someone
     reads in does not decide which market they buy from.

     🔴 Path beats IP: browsing /tw/ from a Korean IP means the shopper asked for
     the Taiwanese page. Same rule as the PLP media resolver — the regression
     that established it is PR #269 (2026-07-23). */
  function effectiveCountry(cached, pathname) {
    var picked = honouredPick();
    if (picked) return picked;
    if (isTwPath(pathname)) return 'TW';
    return String(cached || '').trim().toUpperCase();
  }

  /* Known ceiling: none of this is needed once the markets get a web presence,
     which would make the whole file unnecessary.

     🔴 "Not known yet" and "known, and not the US" are different answers. The IP
     lookup is asynchronous, so until it settles we leave the page as the server
     rendered it rather than guessing — guessing hides the feature from US
     shoppers for as long as the request takes. A pick needs no such wait. */
  function resolved(cached, pathname) {
    if (honouredPick()) return true;
    if (String(cached || '').trim()) return true;
    if (isTwPath(pathname)) return true;
    return !!(window.FoxTheme && window.FoxTheme.countryDetectionComplete === true);
  }

  function listed(el, attr, country) {
    return (el.getAttribute(attr) || '')
      .toUpperCase()
      .split(',')
      .some(function (code) {
        return code.trim() === country;
      });
  }

  function apply(root) {
    var cached = cachedCountry();
    var pathname = window.location.pathname;
    if (!resolved(cached, pathname)) return;

    var country = effectiveCountry(cached, pathname);
    /* 🔴 Detection finished but came back empty. [data-market-only] elements stay
       as the server drew them, and [data-market-except] ones stay hidden.
       That is deliberate: hiding a promo badge from everyone is recoverable,
       showing a US-excluded promo to US shoppers is not.
       ponytail: fails closed on an IP-lookup outage — the whole campaign goes
       dark worldwide. If that trade stops being acceptable, unhide here instead. */
    if (!country) return;

    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[' + ATTR + ']'), function (el) {
      if (!listed(el, ATTR, country)) el.remove();
    });
    Array.prototype.forEach.call(scope.querySelectorAll('[' + EXCEPT_ATTR + ']'), function (el) {
      if (listed(el, EXCEPT_ATTR, country)) el.remove();
      else el.removeAttribute('hidden');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { apply(); }, { once: true });
  } else {
    apply();
  }

  /* The IP answer arrives after first paint on a fresh session. */
  document.addEventListener('country:detected', function () { apply(); });

  /* 🔴 The cart replaces its whole innerHTML on every mutation, even a quantity
     bump (cart.js:484~508), so server-rendered elements come back and have to be
     filtered again. Without this the gate holds until the first quantity change
     and then silently reopens. */
  document.addEventListener('cart:updated', function () { apply(); });

  /* 🔴 PLP filtering/pagination and the recommendation slider inject cards that
     the server drew `hidden`. Nothing else unhides them, so without these the
     promo badge disappears the first time a shopper touches a filter.
     Same event pair product-card-market-media.js listens to. */
  document.addEventListener('collection:rerendered', function () { apply(); });
  document.addEventListener('recommendations:loaded', function () { apply(); }, true);
})();
